import React, { useEffect, useMemo, useRef } from 'react';

/**
 * EmbersOverlay
 *
 * A tiny ambient particle layer to sell torchlight.
 * - Canvas overlay (pointer-events: none)
 * - Low particle counts (cheap)
 * - No external deps
 * - Respects prefers-reduced-motion
 */
export default function EmbersOverlay({ enabled = true, intensity = 1 }) {
  const canvasRef = useRef(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || prefersReducedMotion) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = 0;
    let h = 0;
    let raf = 0;
    let last = performance.now();
    let lastFrameAt = 0;
    const targetFrameMs = 1000 / 30;
    let hidden = typeof document !== 'undefined' ? document.visibilityState !== 'visible' : false;
    const mobile =
      typeof window !== 'undefined' &&
      ((window.matchMedia && window.matchMedia('(max-width: 768px)').matches) || window.innerWidth <= 768);

    const particles = [];
    const maxParticles = mobile ? 72 : 120;

    function clamp(v, a, b) {
      return Math.max(a, Math.min(b, v));
    }

    function resize() {
      w = window.innerWidth;
      h = window.innerHeight;
      const rawDpr = window.devicePixelRatio || 1;
      const dpr = Math.min(rawDpr, mobile ? 1.25 : 1.75);
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function handleVisibilityChange() {
      hidden = document.visibilityState !== 'visible';
      last = performance.now();
      lastFrameAt = 0;
    }

    function spawnOne(t) {
      const p = {
        x: Math.random() * w,
        y: h * (0.55 + Math.random() * 0.45),
        vx: (Math.random() - 0.5) * 18,
        vy: -(35 + Math.random() * 70),
        r: 0.8 + Math.random() * 2.2,
        ttl: 0.9 + Math.random() * 1.4,
        life: 0,
        drift: Math.random() * 1000,
        tw: t,
      };
      particles.push(p);
      if (particles.length > maxParticles) particles.shift();
    }

    let spawnAcc = 0;

    function draw(t) {
      if (hidden) {
        raf = requestAnimationFrame(draw);
        return;
      }

      if (t - lastFrameAt < targetFrameMs) {
        raf = requestAnimationFrame(draw);
        return;
      }
      lastFrameAt = t;

      const dt = clamp((t - last) / 1000, 0, 0.05);
      last = t;

      const I = clamp(intensity, 0, 1.5);
      const active = enabled && I > 0.02;

      // Spawn: low steady rate + occasional bursts.
      if (active) {
        const rate = 2.0 * I; // particles/sec
        spawnAcc += dt * rate;
        while (spawnAcc >= 1) {
          spawnOne(t);
          spawnAcc -= 1;
        }

        // Small random bursts “from time to time”.
        if (Math.random() < dt * (0.18 * I)) {
          const burst = 2 + Math.floor(Math.random() * 5);
          for (let i = 0; i < burst; i++) spawnOne(t);
        }
      }

      ctx.clearRect(0, 0, w, h);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';

      const baseOpacity = active ? (0.35 + I * 0.35) : 0;

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.life += dt;

        if (p.life >= p.ttl) {
          particles.splice(i, 1);
          continue;
        }

        const a = 1 - p.life / p.ttl;
        const fadeIn = Math.min(1, p.life / 0.12);
        const alpha = baseOpacity * a * fadeIn;

        // Motion: upward + tiny sideways drift.
        p.x += p.vx * dt + Math.sin((t + p.drift) / 280) * 12 * dt;
        p.y += p.vy * dt;

        // Slight damping so they don’t streak.
        p.vx *= 0.995;

        // Wrap horizontally to avoid popping.
        if (p.x < -50) p.x = w + 50;
        if (p.x > w + 50) p.x = -50;

        const glow = p.r * 5.5;
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, glow);
        g.addColorStop(0, `rgba(255, 200, 110, ${alpha})`);
        g.addColorStop(0.45, `rgba(255, 120, 40, ${alpha * 0.55})`);
        g.addColorStop(1, `rgba(0, 0, 0, 0)`);

        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, glow, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
      raf = requestAnimationFrame(draw);
    }

    resize();
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, intensity, prefersReducedMotion]);

  if (prefersReducedMotion) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 20,
        pointerEvents: 'none',
        mixBlendMode: 'screen',
      }}
    />
  );
}
